import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getSubscriptionState, isAdmin } from '@/utils/subscription';
import { memberApi } from './api';
import { listLocalConversations } from './localStore';

const AppContext = createContext(null);

export function useMemberApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useMemberApp must be used inside AppProvider');
  return ctx;
}

export default function AppProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [freeRemaining, setFreeRemaining] = useState(0);
  const [credits, setCredits] = useState(null);
  const [careProfile, setCareProfile] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [upgradeOpen, setUpgradeOpenState] = useState(false);
  const [lifetimeOpen, setLifetimeOpen] = useState(false);
  const [pricingFocus, setPricingFocus] = useState('monthly');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [boot, sub] = await Promise.all([
        memberApi.bootstrap(),
        getSubscriptionState(user),
      ]);
      const pro = !!boot.is_pro || !!sub.isActive || isAdmin(user);
      setIsPro(pro);
      setFreeRemaining(0);
      setCredits(boot.credits || null);
      setCareProfile(boot.care_profile || null);
      const local = listLocalConversations(user);
      const merged = new Map();
      [...(boot.conversations || []), ...local].forEach((row) => {
        if (row?.id) merged.set(row.id, row);
      });
      setConversations(
        [...merged.values()].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))
      );
    } catch (error) {
      const sub = await getSubscriptionState(user).catch(() => ({ isActive: false }));
      setIsPro(!!sub.isActive || isAdmin(user));
      setCareProfile(null);
      setConversations(listLocalConversations(user));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (loading) return;
    if (isPro || isAdmin(user)) {
      setLifetimeOpen(false);
      setUpgradeOpenState(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === '1') {
      setUpgradeOpenState(true);
      return;
    }
    const seen = sessionStorage.getItem('byonco:lifetime:seen');
    if (seen) return;
    const timer = window.setTimeout(() => setLifetimeOpen(true), 900);
    return () => window.clearTimeout(timer);
  }, [loading, isPro, user]);

  const entitled = isPro || isAdmin(user);

  const setUpgradeOpen = useCallback((next) => {
    const open = typeof next === 'function' ? next(upgradeOpen) : next;
    if (open && (isPro || isAdmin(user))) {
      setUpgradeOpenState(false);
      return;
    }
    setUpgradeOpenState(!!open);
  }, [isPro, user, upgradeOpen]);

  const requirePro = useCallback(() => {
    if (isPro || isAdmin(user)) return true;
    setUpgradeOpen(true);
    return false;
  }, [isPro, user, setUpgradeOpen]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isPro: entitled,
      freeRemaining,
      setFreeRemaining,
      credits,
      careProfile,
      conversations,
      setConversations,
      refresh,
      upgradeOpen,
      setUpgradeOpen,
      lifetimeOpen,
      setLifetimeOpen,
      pricingFocus,
      setPricingFocus,
      feedbackOpen,
      setFeedbackOpen,
      sidebarOpen,
      setSidebarOpen,
      requirePro,
    }),
    [
      user,
      loading,
      entitled,
      freeRemaining,
      credits,
      careProfile,
      conversations,
      refresh,
      upgradeOpen,
      setUpgradeOpen,
      lifetimeOpen,
      pricingFocus,
      feedbackOpen,
      sidebarOpen,
      requirePro,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
